import { useState, useEffect } from 'react';

interface DatePriceInfo {
  price: number;
  status: string;
  halfPrice?: number | null; // prezzo mezza giornata per-data (stagione → fallback barca), già markup-ato
}

interface UseDatePricesResult {
  priceMap: Record<string, DatePriceInfo>;
  basePrice: number | null;
  loading: boolean;
}

export function useDatePrices(
  boatId: string | undefined,
  year: number,
  month: number,
  departurePointId?: string
): UseDatePricesResult {
  const [priceMap, setPriceMap] = useState<Record<string, DatePriceInfo>>({});
  const [basePrice, setBasePrice] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!boatId) {
      setPriceMap({});
      setBasePrice(null);
      return;
    }

    const controller = new AbortController();

    const fetchPrices = async () => {
      setLoading(true);
      try {
        // Fetch 3 months from current view
        const startDate = new Date(year, month, 1);
        const endDate = new Date(year, month + 3, 0);
        const startStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-01`;
        const endStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;

        // with_markup=1: prezzi cliente (vendor netto + commissione piattaforma,
        // arrotondamento al multiplo di 5 superiore — coerente con /api/bookings).
        // NEXT_PUBLIC_API_URL già contiene '/api' → no doppio prefisso.
        const apiBase = process.env.NEXT_PUBLIC_API_URL || '/api';
        // departure_point_id: prezzi-cella del layer porto scelto (F-B), coerenti col totale motore.
        const portQ = departurePointId ? `&departure_point_id=${encodeURIComponent(departurePointId)}` : '';
        const res = await fetch(
          `${apiBase}/boats/${boatId}/date-prices?start_date=${startStr}&end_date=${endStr}&with_markup=1${portQ}`,
          { signal: controller.signal, cache: 'no-store' },
        );

        if (!res.ok) throw new Error('Failed to fetch prices');

        const json = await res.json();
        const data = json.data;

        setBasePrice(data.basePrice ?? null);

        const map: Record<string, DatePriceInfo> = {};
        (data.datePrices || []).forEach((dp: any) => {
          map[dp.date] = {
            price: dp.price,
            status: dp.status || 'AVAILABLE',
            halfPrice: dp.price_half_day ?? null,
          };
        });
        setPriceMap(map);
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          console.error('Error fetching date prices:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPrices();
    return () => controller.abort();
  }, [boatId, year, month, departurePointId]);

  return { priceMap, basePrice, loading };
}
